import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ChevronRight, MapPin, ExternalLink, Verified, TrendingUp, Sparkles, Clock, Star, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import { resolveImageUrl } from '@/utils/imageUrl';
import { OptimizedImage } from '@/components/OptimizedImage';

const API = `${window.location.origin}/api`;

const LOGO_URL = "/blackified-logo.png";

const PLACEHOLDER_IMAGES = [
  "https://images.pexels.com/photos/10375895/pexels-photo-10375895.jpeg",
  "https://images.unsplash.com/photo-1569598525846-cc400d20fac4?w=800",
  "https://images.pexels.com/photos/33871730/pexels-photo-33871730.jpeg",
  "https://images.unsplash.com/photo-1709044472172-9b5af8b4491e?w=800"
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get user's location for nearby recommendations
        let locationParams = '';
        if (navigator.geolocation) {
          try {
            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
            });
            locationParams = `?lat=${position.coords.latitude}&lng=${position.coords.longitude}`;
          } catch (e) {
            // Geolocation not available or denied
          }
        }

        const [bizRes, catRes, trendingRes, recsRes] = await Promise.all([
          fetch(`${API}/businesses/featured`),
          fetch(`${API}/categories`),
          fetch(`${API}/trending`),
          fetch(`${API}/homepage-recommendations${locationParams}`, { credentials: 'include' })
        ]);
        
        if (bizRes.ok) {
          const data = await bizRes.json();
          setFeaturedBusinesses(data.businesses || []);
        }
        
        if (catRes.ok) {
          const cats = await catRes.json();
          setCategories(Array.isArray(cats) ? cats : cats.categories || []);
        }
        
        if (trendingRes.ok) {
          const trending = await trendingRes.json();
          setTrendingSearches(trending.trending || []);
        }
        
        if (recsRes.ok) {
          const recs = await recsRes.json();
          setRecommendations(recs);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Track the search
      fetch(`${API}/trending/track?query=${encodeURIComponent(searchQuery)}`, { method: 'POST' });
      navigate(`/businesses?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleTrendingClick = (query) => {
    // Track the click
    fetch(`${API}/trending/track?query=${encodeURIComponent(query)}`, { method: 'POST' });
    navigate(`/businesses?search=${encodeURIComponent(query)}`);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative starfield-bg min-h-[85vh] flex flex-col items-center justify-center px-6">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <img 
            src={LOGO_URL} 
            alt="Blackified - Everything Black" 
            className="h-24 md:h-32 lg:h-40 object-contain"
            data-testid="hero-logo"
          />
        </motion.div>

        {/* Search Bar */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={handleSearch}
          className="w-full max-w-2xl mb-8"
        >
          <div className="relative search-glow rounded-full">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Black-owned businesses..."
              className="w-full h-14 md:h-16 bg-zinc-900/80 border border-zinc-800 text-white placeholder:text-zinc-600 rounded-full px-6 pr-14 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/50 transition-all"
              data-testid="hero-search-input"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center text-black hover:bg-zinc-200 transition-colors"
              data-testid="hero-search-button"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>
        </motion.form>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-zinc-500 font-mono text-sm tracking-wider"
        >
          Discover. Support. Empower.
        </motion.p>

        {/* Quick Category Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-wrap justify-center gap-3 mt-8"
        >
          {categories.slice(0, 5).map((category, index) => (
            <Link
              key={category.id || index}
              to={`/businesses?category=${encodeURIComponent(category.id)}`}
              className="px-4 py-2 rounded-full border border-zinc-800 text-zinc-400 text-sm hover:border-zinc-600 hover:text-white transition-colors"
              data-testid={`category-link-${category.id}`}
            >
              {category.name}
            </Link>
          ))}
        </motion.div>

        {/* Trending Searches */}
        {trendingSearches.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <span className="font-mono text-xs uppercase tracking-wider text-zinc-500">Trending Searches</span>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {trendingSearches.slice(0, 6).map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleTrendingClick(item.query)}
                  className="group flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/50 border border-zinc-800 hover:border-purple-500/50 hover:bg-purple-500/10 transition-all"
                  data-testid={`trending-${index}`}
                >
                  {item.is_curated && <Sparkles className="w-3 h-3 text-purple-400" />}
                  <span className="text-sm text-zinc-400 group-hover:text-white transition-colors">
                    {item.query}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </section>

      {/* Featured Businesses Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Featured
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                Verified Businesses
              </h2>
            </div>
            <Link
              to="/businesses"
              className="hidden md:flex items-center gap-2 text-zinc-400 hover:text-white transition-colors group"
              data-testid="view-all-businesses-link"
            >
              <span className="font-mono text-sm">View All</span>
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {loading ? (
            <div className="bento-grid">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-[4/3] skeleton rounded-lg" />
              ))}
            </div>
          ) : featuredBusinesses.length > 0 ? (
            <div className="bento-grid">
              {featuredBusinesses.map((business, index) => (
                <motion.div
                  key={business.business_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`business-card rounded-lg overflow-hidden ${index === 0 ? 'featured-large' : ''}`}
                >
                  <Link to={`/businesses/${business.business_id}`} data-testid={`business-card-${business.business_id}`}>
                    <div className="relative aspect-[4/3]">
                      <OptimizedImage
                        src={business.image_url}
                        fallback={PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]}
                        alt={business.name}
                        preset="card"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                      {/* Verified Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="verified-badge">
                          <Verified className="w-3 h-3" />
                          Verified
                        </span>
                      </div>

                      {/* Business Info */}
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <p className="category-tag mb-2">{business.category}</p>
                        <h3 className="text-xl md:text-2xl font-semibold text-white mb-2">
                          {business.name}
                        </h3>
                        <div className="flex items-center gap-2 text-zinc-400 text-sm">
                          <MapPin className="w-4 h-4" />
                          <span>{business.city || business.address?.split(',')[0] || 'Location'}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-zinc-500 mb-4">No featured businesses yet.</p>
              <Link to="/submit-business" className="btn-primary inline-block" data-testid="submit-first-business-cta">
                Be the first to list your business
              </Link>
            </div>
          )}

          {/* Mobile View All Link */}
          <div className="md:hidden mt-8 text-center">
            <Link
              to="/businesses"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="view-all-businesses-link-mobile"
            >
              View All Businesses
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Personalized Recommendations Section (for logged-in users) */}
      {recommendations && recommendations.personalized && (
        <section className="py-16 px-6 border-t border-zinc-900">
          <div className="max-w-7xl mx-auto">
            {/* For You Section */}
            {recommendations.for_you && recommendations.for_you.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-16"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Personalized</p>
                    <h3 className="text-xl font-semibold text-white">Recommended For You</h3>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {recommendations.for_you.slice(0, 4).map((business, index) => (
                    <Link
                      key={business.business_id}
                      to={`/businesses/${business.business_id}`}
                      className="group glass-card rounded-lg overflow-hidden hover:border-zinc-700 transition-all"
                      data-testid={`rec-for-you-${index}`}
                    >
                      <div className="relative aspect-video">
                        <OptimizedImage
                          src={business.image_url}
                          fallback={PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]}
                          alt={business.name}
                          preset="card"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <p className="text-xs text-purple-400 mb-1">{business.recommendation_reason}</p>
                          <h4 className="text-white font-medium truncate">{business.name}</h4>
                          <p className="text-zinc-400 text-sm truncate">{business.category}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Recently Viewed Section */}
            {recommendations.recently_viewed && recommendations.recently_viewed.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mb-16"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Pick up where you left off</p>
                    <h3 className="text-xl font-semibold text-white">Recently Viewed</h3>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {recommendations.recently_viewed.slice(0, 4).map((business, index) => (
                    <Link
                      key={business.business_id}
                      to={`/businesses/${business.business_id}`}
                      className="group glass-card rounded-lg overflow-hidden hover:border-zinc-700 transition-all"
                      data-testid={`rec-recently-viewed-${index}`}
                    >
                      <div className="relative aspect-video">
                        <OptimizedImage
                          src={business.image_url}
                          fallback={PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]}
                          alt={business.name}
                          preset="card"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h4 className="text-white font-medium truncate">{business.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            {business.average_rating > 0 && (
                              <span className="flex items-center gap-1 text-yellow-400 text-sm">
                                <Star className="w-3 h-3 fill-current" />
                                {business.average_rating.toFixed(1)}
                              </span>
                            )}
                            <span className="text-zinc-500 text-sm">{business.category}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Discover Section */}
            {recommendations.discover && recommendations.discover.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mb-16"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Hidden gems</p>
                    <h3 className="text-xl font-semibold text-white">Discover Something New</h3>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {recommendations.discover.slice(0, 4).map((business, index) => (
                    <Link
                      key={business.business_id}
                      to={`/businesses/${business.business_id}`}
                      className="group glass-card rounded-lg overflow-hidden hover:border-zinc-700 transition-all"
                      data-testid={`rec-discover-${index}`}
                    >
                      <div className="relative aspect-video">
                        <OptimizedImage
                          src={business.image_url}
                          fallback={PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]}
                          alt={business.name}
                          preset="card"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <span className="inline-block px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full mb-2">New</span>
                          <h4 className="text-white font-medium truncate">{business.name}</h4>
                          <p className="text-zinc-500 text-sm truncate">{business.category}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Nearby Section */}
            {recommendations.nearby && recommendations.nearby.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">In your area</p>
                    <h3 className="text-xl font-semibold text-white">Near You</h3>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {recommendations.nearby.slice(0, 4).map((business, index) => (
                    <Link
                      key={business.business_id}
                      to={`/businesses/${business.business_id}`}
                      className="group glass-card rounded-lg overflow-hidden hover:border-zinc-700 transition-all"
                      data-testid={`rec-nearby-${index}`}
                    >
                      <div className="relative aspect-video">
                        <OptimizedImage
                          src={business.image_url}
                          fallback={PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]}
                          alt={business.name}
                          preset="card"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h4 className="text-white font-medium truncate">{business.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <MapPin className="w-3 h-3 text-orange-400" />
                            <span className="text-zinc-400 text-sm truncate">{business.city}, {business.state}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-6 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
              For Business Owners
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-6">
              List Your Business
            </h2>
            <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
              Join our growing directory of verified Black-owned businesses. 
              Get discovered by customers who want to support your success.
            </p>
            <Link
              to="/submit-business"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="cta-submit-business"
            >
              Submit Your Business
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img 
              src={LOGO_URL} 
              alt="Blackified" 
              className="h-8 object-contain opacity-60"
            />
            <p className="font-mono text-xs text-zinc-600">
              © {new Date().getFullYear()} Blackified. Everything Black.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
